import { apiClient } from '@/services/api/client';
import type {
  SearchRequest,
  SearchResponse,
  SearchSuggestResponse,
  RecentSearch,
  SavedSearch,
  SaveSearchRequest,
  UpdateSavedSearchRequest,
} from '../types';

export const searchApi = {
  // Simple search with query params
  async search(params: SearchRequest): Promise<SearchResponse> {
    const searchParams = new URLSearchParams();
    searchParams.set('q', params.query);

    if (params.fields?.length) {
      params.fields.forEach((f) => searchParams.append('fields', f));
    }
    if (params.engine_types?.length) {
      params.engine_types.forEach((e) => searchParams.append('engine_type', e));
    }
    if (params.statuses?.length) {
      params.statuses.forEach((s) => searchParams.append('status', s));
    }
    if (params.tag_ids?.length) {
      params.tag_ids.forEach((t) => searchParams.append('tag_id', t));
    }
    if (params.category_id) {
      searchParams.set('category_id', params.category_id);
    }
    if (params.created_by) {
      searchParams.set('created_by', params.created_by);
    }
    if (params.created_after) {
      searchParams.set('created_after', params.created_after);
    }
    if (params.created_before) {
      searchParams.set('created_before', params.created_before);
    }
    if (params.updated_after) {
      searchParams.set('updated_after', params.updated_after);
    }
    if (params.updated_before) {
      searchParams.set('updated_before', params.updated_before);
    }
    if (params.limit !== undefined) {
      searchParams.set('limit', String(params.limit));
    }
    if (params.offset !== undefined) {
      searchParams.set('offset', String(params.offset));
    }
    if (params.sort_by) {
      searchParams.set('sort_by', params.sort_by);
    }
    if (params.sort_order) {
      searchParams.set('sort_order', params.sort_order);
    }
    if (params.include_highlights) {
      searchParams.set('include_highlights', 'true');
    }
    if (params.include_facets) {
      searchParams.set('include_facets', 'true');
    }

    const response = await apiClient.get<SearchResponse>(
      `/tenant/search?${searchParams.toString()}`
    );
    return response;
  },

  // Advanced search with request body
  async advancedSearch(request: SearchRequest): Promise<SearchResponse> {
    const response = await apiClient.post<SearchResponse>('/tenant/search', request);
    return response;
  },

  // Get autocomplete suggestions
  async suggest(query: string, limit?: number): Promise<SearchSuggestResponse> {
    const searchParams = new URLSearchParams();
    searchParams.set('q', query);
    if (limit !== undefined) {
      searchParams.set('limit', String(limit));
    }

    const response = await apiClient.get<SearchSuggestResponse>(
      `/tenant/search/suggest?${searchParams.toString()}`
    );
    return response;
  },

  // Get recent searches
  async getRecentSearches(limit?: number): Promise<RecentSearch[]> {
    const searchParams = new URLSearchParams();
    if (limit !== undefined) {
      searchParams.set('limit', String(limit));
    }

    const query = searchParams.toString();
    const url = `/tenant/searches/recent${query ? `?${query}` : ''}`;

    const response = await apiClient.get<{ searches: RecentSearch[] }>(url);
    return response.searches;
  },

  // Clear recent searches
  async clearRecentSearches(): Promise<void> {
    await apiClient.delete('/tenant/searches/recent');
  },

  // List saved searches
  async listSavedSearches(): Promise<SavedSearch[]> {
    const response = await apiClient.get<{ searches: SavedSearch[] }>(
      '/tenant/searches/saved'
    );
    return response.searches;
  },

  // Save a search
  async saveSearch(request: SaveSearchRequest): Promise<SavedSearch> {
    const response = await apiClient.post<SavedSearch>(
      '/tenant/searches/saved',
      request
    );
    return response;
  },

  // Get a saved search
  async getSavedSearch(id: string): Promise<SavedSearch> {
    const response = await apiClient.get<SavedSearch>(
      `/tenant/searches/saved/${id}`
    );
    return response;
  },

  // Update a saved search
  async updateSavedSearch(
    id: string,
    request: UpdateSavedSearchRequest
  ): Promise<SavedSearch> {
    const response = await apiClient.put<SavedSearch>(
      `/tenant/searches/saved/${id}`,
      request
    );
    return response;
  },

  // Delete a saved search
  async deleteSavedSearch(id: string): Promise<void> {
    await apiClient.delete(`/tenant/searches/saved/${id}`);
  },

  // Execute a saved search
  async executeSavedSearch(id: string): Promise<SearchResponse> {
    const response = await apiClient.post<SearchResponse>(
      `/tenant/searches/saved/${id}/execute`,
      {}
    );
    return response;
  },

  // Reindex a workflow
  async reindexWorkflow(workflowId: string): Promise<void> {
    await apiClient.post(`/tenant/workflows/${workflowId}/reindex`, {});
  },

  // Reindex all workflows
  async reindexAll(): Promise<void> {
    await apiClient.post('/tenant/search/reindex', {});
  },
};
